// HOC Bear Hunt troop optimizer
// Rules:
//  - Max 6 marches/formations
//  - Each formation holds equal Cavalry & Archers (paired 1:1)
//  - Remaining capacity (up to troop limit) filled with Infantry (min 1,000 when available)
//  - Surplus Cav/Arch (if totals mismatch) and surplus Infantry go to leftover

const HERO_SLOTS = [
  { name: 'Chenko',  title: 'Captain', suit: 'spade'   },
  { name: 'Yeonwoo', title: 'Captain', suit: 'heart'   },
  { name: 'Amane',   title: 'Captain', suit: 'diamond' },
];

const TIERS = ['L10', 'L9', 'L8', 'L7', 'L6'];

const MAX_FORMATIONS = 6;

// Split integer amount across N parts as evenly as possible. Mode 'even' distributes remainder round-robin.
// Mode 'strong' loads formation 1 first (used by Royal Flush for tier assignment).
function distribute(amount, parts, mode = 'even') {
  if (parts <= 0) return [];
  const out = Array(parts).fill(0);
  if (amount <= 0) return out;
  if (mode === 'even') {
    const base = Math.floor(amount / parts);
    const rem = amount - base * parts;
    for (let i = 0; i < parts; i++) out[i] = base + (i < rem ? 1 : 0);
    return out;
  }
  return null;
}

function toInt(v) { return parseInt(v) || 0; }

function sumTiers(obj) {
  return TIERS.reduce((a, t) => a + toInt(obj[t]), 0);
}

function emptyTierObj() {
  return TIERS.reduce((o, t) => { o[t] = 0; return o; }, {});
}

function copyTiers(src) {
  return TIERS.reduce((o, t) => { o[t] = toInt(src[t]); return o; }, {});
}

export function optimize({ troops, limit, mode, infPer }) {
  const L = Math.max(1, toInt(limit));
  const INF_PER = Math.max(0, toInt(infPer));
  const cav  = copyTiers(troops.cavalry);
  const arch = copyTiers(troops.archers);
  const inf  = copyTiers(troops.infantry);

  const totalCav  = sumTiers(cav);
  const totalArch = sumTiers(arch);
  const totalInf  = sumTiers(inf);

  const totalNonInf = totalCav + totalArch;

  if (totalNonInf === 0 && totalInf === 0) {
    return {
      formations: [], finalN: 0, limit: L, mode, infPer: INF_PER,
      leftover: emptyLeftover(cav, arch, inf),
      totals: { totalCav, totalArch, totalInf, paired: 0, pairedUsed: 0 },
    };
  }

  // Formation capacity for Cavalry + Archers (Infantry seat is fixed).
  const nonInfCapPerF = Math.max(0, L - INF_PER);

  // Determine how many formations we can fill fully.
  // Prefer MAX_FORMATIONS, but if non-inf supply is short, use fewer so each formation is still full.
  let N;
  if (nonInfCapPerF === 0) {
    N = Math.min(MAX_FORMATIONS, Math.max(1, Math.ceil(totalInf / L)));
  } else if (totalNonInf === 0) {
    N = Math.min(MAX_FORMATIONS, Math.max(1, Math.ceil(totalInf / L)));
  } else {
    const fullFormations = Math.floor(totalNonInf / nonInfCapPerF);
    N = Math.min(MAX_FORMATIONS, Math.max(1, fullFormations || 1));
  }

  // Non-inf troops to allocate across N formations (every formation stays at exactly L when supply allows).
  const totalNonInfBudget = N * nonInfCapPerF;
  // Fill priority: ARCHERS first (best damage), then CAVALRY to fill the rest.
  const archTotalUsed = Math.min(totalArch, totalNonInfBudget);
  const cavTotalUsed  = Math.min(totalCav,  Math.max(0, totalNonInfBudget - archTotalUsed));
  const actualNonInfUsed = archTotalUsed + cavTotalUsed;

  // Per-formation cav/arch counts. Arch remainder lands at the START, cav remainder at the END,
  // so every formation ends up with the same total (arch rem + cav rem = N when fully filled).
  const archPerArr = Array.from({ length: N }, (_, i) => {
    const base = Math.floor(archTotalUsed / N);
    const rem = archTotalUsed - base * N;
    return base + (i < rem ? 1 : 0);
  });
  const cavPerArr = Array.from({ length: N }, (_, i) => {
    const base = Math.floor(cavTotalUsed / N);
    const rem = cavTotalUsed - base * N;
    return base + (i >= N - rem ? 1 : 0);
  });

  // Each formation gets EXACTLY INF_PER infantry when supply allows (even split when short).
  const infNeeded = INF_PER * N;
  const infToUse = Math.min(totalInf, infNeeded);
  const infPerArr = Array(N).fill(0);
  if (infToUse > 0 && N > 0) {
    const base = Math.floor(infToUse / N);
    const rem = infToUse - base * N;
    for (let i = 0; i < N; i++) infPerArr[i] = base + (i < rem ? 1 : 0);
  }

  function assignTiers(poolTiers, perFormation, modeLocal) {
    const buckets = Array.from({ length: perFormation.length }, () => emptyTierObj());
    if (modeLocal === 'strong-first') {
      let fIdx = 0;
      for (const t of poolTiers) {
        let remaining = t.count;
        while (remaining > 0 && fIdx < perFormation.length) {
          const curSum = TIERS.reduce((a, tt) => a + buckets[fIdx][tt], 0);
          const need = perFormation[fIdx] - curSum;
          if (need <= 0) { fIdx += 1; continue; }
          const take = Math.min(need, remaining);
          buckets[fIdx][t.tier] += take;
          remaining -= take;
          const newSum = curSum + take;
          if (newSum >= perFormation[fIdx]) fIdx += 1;
        }
      }
    } else {
      for (const t of poolTiers) {
        const parts = distribute(t.count, perFormation.length, 'even');
        for (let i = 0; i < perFormation.length; i++) {
          buckets[i][t.tier] += parts[i];
        }
      }
    }
    // Reconcile: ensure each formation sums to perFormation[i] by moving stray units
    // between formations of the same type (preserves total pool).
    reconcileBuckets(buckets, perFormation);
    return buckets;
  }

  function reconcileBuckets(buckets, perFormation) {
    const N = perFormation.length;
    const safetyLimit = N * 10 + 10;
    for (let pass = 0; pass < safetyLimit; pass++) {
      let maxOver = 0, overIdx = -1;
      let maxUnder = 0, underIdx = -1;
      for (let i = 0; i < N; i++) {
        const sum = TIERS.reduce((a, t) => a + buckets[i][t], 0);
        const diff = sum - perFormation[i];
        if (diff > maxOver)  { maxOver  = diff; overIdx  = i; }
        if (diff < -maxUnder) { maxUnder = -diff; underIdx = i; }
      }
      if (overIdx < 0 || underIdx < 0 || overIdx === underIdx) break;
      let moved = false;
      for (const t of TIERS) {
        if (buckets[overIdx][t] > 0) {
          buckets[overIdx][t] -= 1;
          buckets[underIdx][t] += 1;
          moved = true;
          break;
        }
      }
      if (!moved) break;
    }
  }

  function trimPool(pool, target, strategy) {
    const total = TIERS.reduce((a, t) => a + pool[t], 0);
    const out = emptyTierObj();
    if (target <= 0 || total <= 0) {
      return { used: out, surplus: { ...pool } };
    }
    if (strategy === 'strong-first') {
      let remaining = target;
      for (const t of TIERS) {
        const take = Math.min(pool[t], remaining);
        out[t] = take;
        remaining -= take;
      }
    } else {
      let remaining = target;
      for (const t of TIERS) {
        const share = Math.floor((pool[t] / total) * target);
        out[t] = Math.min(share, pool[t]);
        remaining -= out[t];
      }
      for (const t of TIERS) {
        if (remaining <= 0) break;
        const canAdd = pool[t] - out[t];
        const add = Math.min(canAdd, remaining);
        out[t] += add;
        remaining -= add;
      }
    }
    const surplus = emptyTierObj();
    for (const t of TIERS) surplus[t] = pool[t] - out[t];
    return { used: out, surplus };
  }

  const trimStrategy = mode === 'strong-first' ? 'strong-first' : 'proportional';
  const cavTrim  = trimPool(cav,  cavTotalUsed,  trimStrategy);
  const archTrim = trimPool(arch, archTotalUsed, trimStrategy);

  const cavPool  = TIERS.map((t) => ({ tier: t, count: cavTrim.used[t]  }));
  const archPool = TIERS.map((t) => ({ tier: t, count: archTrim.used[t] }));

  const infTrim = trimPool(inf, infToUse, trimStrategy);
  const usedInfPool = TIERS.map((t) => ({ tier: t, count: infTrim.used[t] }));
  const infSurplus = infTrim.surplus;

  const cavBuckets  = assignTiers(cavPool,     cavPerArr,  mode);
  const archBuckets = assignTiers(archPool,    archPerArr, mode);
  const infBuckets  = assignTiers(usedInfPool, infPerArr,  mode);

  const formations = [];
  for (let i = 0; i < N; i++) {
    const items = [];
    for (const t of TIERS) {
      if (infBuckets[i][t] > 0) items.push({ type: 'infantry', tier: t, count: infBuckets[i][t] });
    }
    for (const t of TIERS) {
      if (cavBuckets[i][t] > 0) items.push({ type: 'cavalry', tier: t, count: cavBuckets[i][t] });
    }
    for (const t of TIERS) {
      if (archBuckets[i][t] > 0) items.push({ type: 'archers', tier: t, count: archBuckets[i][t] });
    }
    const total = items.reduce((a, b) => a + b.count, 0);
    formations.push({
      index: i + 1,
      items,
      total,
      hero: i < HERO_SLOTS.length ? HERO_SLOTS[i] : null,
      archPerFormation: archPerArr[i],
      cavPerFormation: cavPerArr[i],
      infPerFormation: infPerArr[i],
    });
  }

  const leftover = {
    cavalry: cavTrim.surplus,
    archers: archTrim.surplus,
    infantry: infSurplus,
    totalCav:  sumTiers(cavTrim.surplus),
    totalArch: sumTiers(archTrim.surplus),
    totalInf:  sumTiers(infSurplus),
  };

  return {
    formations,
    finalN: N,
    limit: L,
    mode,
    infPer: INF_PER,
    leftover,
    totals: {
      totalCav, totalArch, totalInf,
      archUsed: archTotalUsed,
      cavUsed:  cavTotalUsed,
      pairedUsed: Math.min(archTotalUsed, cavTotalUsed),
    },
  };
}

function emptyLeftover(cav, arch, inf) {
  return {
    cavalry: { ...cav },
    archers: { ...arch },
    infantry: { ...inf },
    totalCav:  sumTiers(cav),
    totalArch: sumTiers(arch),
    totalInf:  sumTiers(inf),
  };
}

export { HERO_SLOTS, MAX_FORMATIONS, TIERS };
